import Modal from "../../common/Modal/Modal";
import Badge from "../../common/Badge/Badge";
import { badgeValidacion } from "../../common/ValidacionesHistorico/ValidacionesHistorico";
import { formatDate } from "../../../utils/utils";
import {
    formatPesoResiduo,
    pesoEnUnidadResiduo,
    unidadResiduo,
} from "../../../utils/format";
import "./ResiduoDetalleModal.scss";

const VACIO = "—";

/* Los catálogos vienen de mapList, así que cada opción es {value, label, id}. */
const etiqueta = (lista, cod) => {
    if (cod == null || !Array.isArray(lista)) return null;

    const opcion = lista.find(
        (o) => String(o.value ?? o.id) === String(cod)
    );

    return opcion?.label ?? null;
};

const texto = (valor) => {
    if (valor == null) return VACIO;

    const limpio = String(valor).trim();

    return limpio === "" ? VACIO : limpio;
};

/* Igual que la columna "document" de Table.tsx: el backend manda el fichero en
   base64 (no se sirve wwwroot como estatico), así que el link se abre con un
   data URI y se descarga con el nombre original. */
const documento = (nombre, base64) => {
    const limpio = texto(nombre);
    if (limpio === VACIO || !base64) return limpio;

    return (
        <a
            href={`data:application/pdf;base64,${base64}`}
            target="_blank"
            rel="noopener noreferrer"
            download={limpio}
        >
            {limpio}
        </a>
    );
};

const fecha = (valor) => {
    if (!valor) return VACIO;

    const formateada = formatDate(valor);

    return formateada === "N/A" ? VACIO : formateada;
};

const numero = (valor, sufijo = "") => {
    if (valor == null || valor === "") return VACIO;

    const num = Number(valor);
    if (!Number.isFinite(num)) return VACIO;

    return `${new Intl.NumberFormat("es-ES", {
        maximumFractionDigits: 2,
    }).format(num)}${sufijo}`;
};

const nombreTratamiento = (tratamiento) => {
    if (!tratamiento?.codTipoTratamiento) return VACIO;

    /* El tratamiento se identifica por su código (D5, R3...) y se describe con
       tipo y descripción; se muestra lo que haya. */
    const partes = [
        tratamiento.codigo,
        tratamiento.descripcion || tratamiento.subtipo || tratamiento.tipo,
    ].filter((p) => p && String(p).trim() !== "");

    return partes.length > 0 ? partes.join(" - ") : VACIO;
};

const Campo = ({ label, children, ancho }) => (
    <div className={`residuo-detalle__campo${ancho ? " residuo-detalle__campo--ancho" : ""}`}>
        <span className="residuo-detalle__campo-label">{label}</span>
        <span className="residuo-detalle__campo-valor">{children}</span>
    </div>
);

const Seccion = ({ titulo, children }) => (
    <section className="residuo-detalle__seccion">
        <h3 className="residuo-detalle__seccion-titulo">{titulo}</h3>
        <div className="residuo-detalle__campos">{children}</div>
    </section>
);

/**
 * Ficha de solo lectura de una línea de residuo de un centro productor: todo lo
 * que tiene metido, que es bastante más de lo que caben en la tabla.
 *
 * Los importes llegan ya convertidos a la moneda del selector desde GetObra; el
 * formateador se recibe de la ficha para no repetir aquí esa decisión.
 */
export default function ResiduoDetalleModal({
    isOpen,
    onClose,
    residuo,
    catalogos = {},
    formateaCoste,
}) {
    if (!residuo) return null;

    const { proyectos, vehiculos, combustibles, tiposTratamiento } = catalogos;

    const unidad = unidadResiduo(residuo.codTipoResiduo);
    const esPeligroso = unidad === "kg";

    const importe = (valor) =>
        valor == null ? VACIO : formateaCoste.format(valor);

    /* El peso viaja en kg y se muestra en la unidad del tipo de residuo, igual
       que en la tabla. La valorización (kg) y su % los calcula el backend según
       el tratamiento final: si es de eliminación valen 0 aunque el porcentaje
       del tratamiento final sea 100. Vacía = la línea no tiene ese dato. */
    const pesoEnUnidad = pesoEnUnidadResiduo(
        residuo.peso,
        residuo.codTipoResiduo
    );
    const conDatoValorizacion = residuo.valorizacion != null;
    const porcentajeValorizado = Number(residuo.porcentajeValorizacion) || 0;
    const valorizado = pesoEnUnidadResiduo(
        residuo.valorizacion,
        residuo.codTipoResiduo
    );

    const badge = badgeValidacion(residuo.codEstado);

    const nombreResiduo = [residuo.residuo?.codigo, residuo.residuo?.nombre]
        .filter((p) => p && String(p).trim() !== "" && p !== "-")
        .join(" - ");

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            position="side"
            title="Detalle del residuo"
            actions={
                <button
                    className="residuo-detalle__cerrar"
                    onClick={onClose}
                >
                    Cerrar
                </button>
            }
        >
            <div className="residuo-detalle">
                <header className="residuo-detalle__cabecera">
                    <h2>{texto(nombreResiduo)}</h2>
                    <div className="residuo-detalle__cabecera-badges">
                        <Badge variant={badge.type} Icon={badge.Icon}>
                            {badge.label}
                        </Badge>
                        <Badge variant={esPeligroso ? "warning" : "info"}>
                            {residuo.residuo?.tipoResiduo?.descripcion ||
                                (esPeligroso
                                    ? "Peligroso"
                                    : "No peligroso")}
                        </Badge>
                    </div>
                    {residuo.lastValidationNote ? (
                        <p className="residuo-detalle__nota-validacion">
                            {residuo.lastValidationNote}
                        </p>
                    ) : null}
                </header>

                <Seccion titulo="Residuo">
                    <Campo label="Proyecto">
                        {texto(etiqueta(proyectos, residuo.codProyecto))}
                    </Campo>
                    <Campo label="Código de identificación">
                        {texto(residuo.codIdentificacion)}
                    </Campo>
                    <Campo label="Observaciones" ancho>
                        {texto(residuo.observacionesGestionFinal)}
                    </Campo>
                </Seccion>

                <Seccion titulo="Cantidades">
                    <Campo label="Peso">
                        {formatPesoResiduo(
                            residuo.peso,
                            residuo.codTipoResiduo
                        )}
                    </Campo>
                    <Campo label="Volumen">
                        {numero(residuo.volumen, " m³")}
                    </Campo>
                    <Campo label="Valorización">
                        {conDatoValorizacion
                            ? `${numero(valorizado)} / ${numero(
                                  pesoEnUnidad
                              )} ${unidad} (${numero(
                                  porcentajeValorizado,
                                  "%"
                              )})`
                            : "Sin datos"}
                    </Campo>
                    <Campo label="Eliminación">
                        {residuo.eliminacion == null
                            ? VACIO
                            : formatPesoResiduo(
                                  residuo.eliminacion,
                                  residuo.codTipoResiduo
                              )}
                    </Campo>
                </Seccion>

                <Seccion titulo="Transporte">
                    <Campo label="Transportista" ancho>
                        {texto(residuo.transportista?.nombre)}
                    </Campo>
                    <Campo label="Fecha de recogida">
                        {fecha(residuo.fechaTransporte)}
                    </Campo>
                    <Campo label="Nº de documento">
                        {texto(residuo.numDocumentoTransporte)}
                    </Campo>
                    <Campo label="Documento" ancho>
                        {documento(
                            residuo.documentoTransporte,
                            residuo.documentoTransporteBase64
                        )}
                    </Campo>
                    <Campo label="Tipo de vehículo">
                        {texto(etiqueta(vehiculos, residuo.codVehiculo))}
                    </Campo>
                    <Campo label="Combustible">
                        {texto(
                            etiqueta(combustibles, residuo.codCombustible)
                        )}
                    </Campo>
                    <Campo label="Kilómetros">
                        {numero(residuo.numKm, " km")}
                    </Campo>
                    <Campo label="Nº de viajes">
                        {numero(residuo.numViajes)}
                    </Campo>
                </Seccion>

                <Seccion titulo="Gestión intermedia">
                    <Campo label="Gestor intermedio" ancho>
                        {texto(residuo.gestorIntermedio?.nombre)}
                    </Campo>
                    <Campo label="Fecha">
                        {fecha(residuo.fechaGestionIntermedia)}
                    </Campo>
                    <Campo label="Porcentaje">
                        {numero(residuo.porcentaje, "%")}
                    </Campo>
                    <Campo label="Tratamiento" ancho>
                        {residuo.tipoTratamientoIntermedio
                            ?.codTipoTratamiento
                            ? nombreTratamiento(
                                  residuo.tipoTratamientoIntermedio
                              )
                            : texto(
                                  etiqueta(
                                      tiposTratamiento,
                                      residuo.codTipoTratamiento
                                  )
                              )}
                    </Campo>
                </Seccion>

                <Seccion titulo="Gestión final">
                    <Campo label="Gestor final" ancho>
                        {texto(residuo.gestorFinal?.nombre)}
                    </Campo>
                    <Campo label="Fecha de tratamiento">
                        {fecha(residuo.fechaTratamiento)}
                    </Campo>
                    <Campo label="Nº de documento">
                        {texto(residuo.numDocumentoFinal)}
                    </Campo>
                    <Campo label="Documento" ancho>
                        {documento(
                            residuo.documentoFinal,
                            residuo.documentoFinalBase64
                        )}
                    </Campo>
                    <Campo label="Porcentaje">
                        {numero(residuo.porcentaje2, "%")}
                    </Campo>
                    <Campo label="Tratamiento" ancho>
                        {residuo.tipoTratamientoFinal?.codTipoTratamiento
                            ? nombreTratamiento(residuo.tipoTratamientoFinal)
                            : texto(
                                  etiqueta(
                                      tiposTratamiento,
                                      residuo.codTipoTratamiento2
                                  )
                              )}
                    </Campo>
                </Seccion>

                <Seccion titulo="Costes">
                    <Campo label="Transporte">
                        {importe(residuo.costeTransporte)}
                    </Campo>
                    <Campo label="Gestión">
                        {importe(residuo.costeGestion)}
                    </Campo>
                    <Campo label="Colocación de contenedor">
                        {importe(residuo.costeColocacion)}
                    </Campo>
                    <Campo label="Alquiler de contenedor">
                        {importe(residuo.costeAlquiler)}
                    </Campo>
                    <Campo label="Generación de documento">
                        {importe(residuo.costeDocumento)}
                    </Campo>
                    <Campo label="Abono chatarra">
                        {importe(residuo.abonoChatarra)}
                    </Campo>
                    <div className="residuo-detalle__campo residuo-detalle__campo--ancho residuo-detalle__campo--total">
                        <span className="residuo-detalle__campo-label">
                            Coste final
                        </span>
                        <span
                            className={`residuo-detalle__campo-valor coste ${
                                Number(residuo.costeTotal) < 0
                                    ? "coste--negative"
                                    : "coste--positive"
                            }`}
                        >
                            {importe(residuo.costeTotal ?? 0)}
                        </span>
                    </div>
                </Seccion>
            </div>
        </Modal>
    );
}
